import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  KeyRound,
  UserX,
  UserCheck,
  Trash2,
  Edit,
  Upload,
  Download,
  FolderPlus,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ACTION_CONFIG = {
  user_created: { label: 'User Created', icon: UserPlus, color: 'bg-green-50 text-green-700 border-green-200' },
  password_reset: { label: 'Password Reset', icon: KeyRound, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  role_changed: { label: 'Role Changed', icon: Edit, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  user_suspended: { label: 'User Suspended', icon: UserX, color: 'bg-red-50 text-red-700 border-red-200' },
  user_reactivated: { label: 'User Reactivated', icon: UserCheck, color: 'bg-green-50 text-green-700 border-green-200' },
  user_removed: { label: 'User Removed', icon: Trash2, color: 'bg-red-50 text-red-700 border-red-200' },
  bulk_import: { label: 'Bulk Import', icon: Upload, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  users_exported: { label: 'Users Exported', icon: Download, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  project_assigned: { label: 'Project Assigned', icon: FolderPlus, color: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const AdminActivityLogPage = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');
  const limit = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (actionFilter !== 'all') params.action = actionFilter;

      const [logsRes, countRes] = await Promise.all([
        api.get('/admin/audit-log', { params }),
        api.get('/admin/audit-log/count', { params: actionFilter !== 'all' ? { action: actionFilter } : {} })
      ]);
      setEntries(logsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error('Failed to fetch audit log:', error);
      toast.error('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActionBadge = (action) => {
    const config = ACTION_CONFIG[action] || { label: action, color: 'bg-slate-50 text-slate-700 border-slate-200' };
    return (
      <Badge variant="outline" className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getActionIcon = (action) => {
    const config = ACTION_CONFIG[action];
    if (!config) return <Activity className="h-4 w-4 text-slate-400" />;
    const Icon = config.icon;
    return <Icon className="h-4 w-4 text-slate-500" />;
  };

  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Activity Log</h1>
          <p className="text-sm text-slate-500">Track all admin actions and changes</p>
        </div>
        <Badge variant="outline" className="text-slate-500">
          {totalCount} entries
        </Badge>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[200px] h-9">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="user_created">User Created</SelectItem>
            <SelectItem value="password_reset">Password Reset</SelectItem>
            <SelectItem value="role_changed">Role Changed</SelectItem>
            <SelectItem value="user_suspended">User Suspended</SelectItem>
            <SelectItem value="user_reactivated">User Reactivated</SelectItem>
            <SelectItem value="user_removed">User Removed</SelectItem>
            <SelectItem value="bulk_import">Bulk Import</SelectItem>
            <SelectItem value="users_exported">Users Exported</SelectItem>
            <SelectItem value="project_assigned">Project Assigned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Log Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Performed By</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-slate-500">
                    No activity recorded yet.
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{getActionIcon(entry.action)}</TableCell>
                    <TableCell>{getActionBadge(entry.action)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{entry.actor_name}</p>
                        <p className="text-xs text-slate-500">{entry.actor_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {entry.target_name ? (
                        <span className="text-sm text-slate-700">{entry.target_name}</span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500 max-w-xs truncate block">
                        {entry.details || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500 whitespace-nowrap">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivityLogPage;
